import { Component } from "react";
import { IconButton } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import EmailIcon from "@material-ui/icons/Email";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import i18n from "../i18n";
import styles from "./ReportErrorSelector.module.scss";
import UrlService from "../common/UrlService";
import Config from "../common/Config";
import type { ModalChildProps } from './ModalComponent';

export interface ReportErrorSelectorProps extends ModalChildProps {
  /** Asunto ya formateado por quien abre la modal. */
  reportErrorEmailSubject?: string | null;
  /** Mensaje ya formateado por quien abre la modal. */
  reportErrorWhatsappMessage?: string | null;
  /** Titulo de la card desde la que se reporta, si viene de una. */
  reportErrorCardTitle?: string | null;
}

const DEFAULT_EMAIL_SUBJECT_KEY = "reportError.defaultEmailSubject";
const DEFAULT_WHATSAPP_SUBJECT_KEY = "reportError.defaultWhatsappSubject";

export default class ReportErrorSelector extends Component<ReportErrorSelectorProps> {
  constructor(props: ReportErrorSelectorProps) {
    super(props);
    this.handleEmailClick = this.handleEmailClick.bind(this);
    this.handleWhatsappClick = this.handleWhatsappClick.bind(this);
  }

  componentDidMount(): void {
    // Needed to avoid aria-hidden warning when opening the modal from Drawer items.
    const active = document.activeElement as HTMLElement | null;
    if (active && active.blur) {
      active.blur();
    }
  }

  // Returns email subject: uses pre-formatted prop when provided by the opener, otherwise fallback.
  getEmailSubject(): string {
    return this.props.reportErrorEmailSubject || i18n.t(DEFAULT_EMAIL_SUBJECT_KEY);
  }

  // Returns WhatsApp message: uses pre-formatted prop when provided by the opener, otherwise fallback.
  getWhatsappMessage(): string {
    return this.props.reportErrorWhatsappMessage || i18n.t(DEFAULT_WHATSAPP_SUBJECT_KEY);
  }

  // Builds description text by origin: map uses default text; card uses specific prefix plus card title.
  getDescriptionText(): string {
    const cardTitle = (this.props.reportErrorCardTitle || "").trim();
    if (!cardTitle) {
      return i18n.t("reportError.descriptionDefault");
    }

    return `${i18n.t("reportError.descriptionCard", { title: cardTitle})}`;
  }

  // Opens the default email app via window.open with mailto URL.
  handleEmailClick(): void {
    const email = (Config.getReportErrorEmail() || "").trim();
    if (!email) {
      console.warn("report-error: missing report error email config");
      return;
    }

    const subject = encodeURIComponent(this.getEmailSubject());
    const emailUrl = `mailto:${email}?subject=${subject}`;
    window.open(emailUrl, "_blank");

    if (this.props.handleClose) {
      this.props.handleClose();
    }
  }

  // Opens WhatsApp chat with predefined phone and prefilled message.
  handleWhatsappClick(): void {
    const phone = Config.getReportErrorWhatsappPhone() || "";
    // wa.me only accepts digits; config can include '+' for readability.
    const normalizedPhone = phone.replace(/\D/g, "");

    if (!normalizedPhone) {
      console.warn("report-error: missing report error WhatsApp phone config");
      return;
    }

    const whatsappMessage = encodeURIComponent(this.getWhatsappMessage());
    const whatsappUrl = `https://wa.me/${normalizedPhone}?text=${whatsappMessage}`;
    UrlService.openExternalUrl(whatsappUrl);

    if (this.props.handleClose) {
      this.props.handleClose();
    }
  }

  render() {
    return (
      <div className={styles.reportErrorSelector}>
        <Typography variant="h5" className={styles.title}>
          {i18n.t("reportError.title")}
        </Typography>

        <Typography variant="h5" className={styles.description}>
          {this.getDescriptionText()}
        </Typography>

        <div className={styles.buttonsContainer}>
          <div className={styles.iconOption}>
            <IconButton color="primary" onClick={this.handleEmailClick}>
              <EmailIcon className={styles.icon} />
            </IconButton>
            <Typography variant="body1">{i18n.t("reportError.email")}</Typography>
          </div>
          <div className={styles.iconOption}>
            <IconButton onClick={this.handleWhatsappClick}>
              <WhatsAppIcon className={`${styles.icon} ${styles.whatsappIcon}`} />
            </IconButton>
            <Typography variant="body1">{i18n.t("reportError.whatsapp")}</Typography>
          </div>
        </div>
      </div>
    );
  }
}
